import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, Music } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function TalentPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Demo artists

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await axios.get(`${API}/public/artists?limit=50`);
        if (response.data.items?.length > 0) {
          setArtists(response.data.items);
        } else {
          setArtists([]);
        }
      } catch (error) {
        console.error('Error fetching artists:', error);
        setArtists([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArtists();
  }, []);

  const categories = ['all', 'DJ', 'Band', 'Artist', 'Singer', 'Producer'];

  const filteredArtists = artists.filter(artist => {
    const matchesFilter = filter === 'all' || artist.categories?.includes(filter);
    const matchesSearch = searchTerm === '' || 
      (artist.name?.[lang] || artist.name?.es || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      artist.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16" data-testid="talent-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
              {t('talent.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-8">
              {t('talent.title')}
            </motion.h1>

            {/* Filters */}
            <motion.div variants={fadeInUp} className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setFilter(cat)}
                    className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                      filter === cat
                        ? 'bg-white text-black'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`filter-${cat}`}
                  >
                    {cat === 'all' ? t('talent.filter_all') : cat}
                  </button>
                ))}
              </div>

              <div className="relative w-full md:w-auto">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-premium pl-12 pr-4 py-3 w-full md:w-64"
                  data-testid="search-input"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Artists Grid */}
      <section className="section-dark py-16" data-testid="artists-grid">
        <div className="container-premium">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[3/4] loading-skeleton rounded-lg" />
              ))}
            </div>
          ) : filteredArtists.length > 0 ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredArtists.map((artist) => (
                <motion.div key={artist.artist_id || artist.slug} variants={fadeInUp}>
                  <Link to={`/talent/${artist.slug}`} className="block group" data-testid={`artist-${artist.slug}`}>
                    <div className="relative aspect-[3/4] overflow-hidden mb-4">
                      <img
                        src={artist.cover_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80'}
                        alt={artist.name?.[lang] || artist.name?.es}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {artist.featured && (
                        <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-black text-xs font-medium rounded-full">
                          Featured
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {artist.genres?.slice(0, 2).map((genre) => (
                            <span key={genre} className="text-[#D4AF37] text-xs tracking-wider uppercase">
                              {genre}
                            </span>
                          ))}
                        </div>
                        <h3 className="font-display text-xl text-white mb-1">
                          {artist.name?.[lang] || artist.name?.es}
                        </h3>
                        <p className="flex items-center gap-1 text-white/50 text-sm">
                          <MapPin size={14} />
                          {artist.city}, {artist.country}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <Music size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50">No se encontraron artistas</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-subtle" data-testid="talent-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl text-white mb-4">
              ¿Eres artista y quieres unirte?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 mb-8">
              Regístrate y envía tu perfil para revisión
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/register" className="btn-gold">
                Aplicar como Artista
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
